<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useHelpStore } from '@/stores/useHelpStore'
import HelpContent from './HelpContent.vue'
import HelpNavigation from './HelpNavigation.vue'

const route = useRoute()
const helpStore = useHelpStore()
const containerRef = ref<HTMLElement | null>(null)
let previouslyFocusedElement: HTMLElement | null = null

function handleClose() {
  helpStore.close()
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    handleClose()
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    handleClose()
    return
  }

  // Focus trap: keep Tab within modal
  if (event.key === 'Tab' && containerRef.value) {
    const focusable = containerRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

watch(() => helpStore.isOpen, async (open) => {
  if (open) {
    previouslyFocusedElement = document.activeElement as HTMLElement
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeydown)
    // Focus the close button after render
    await nextTick()
    const closeBtn = containerRef.value?.querySelector<HTMLElement>('.close-button')
    closeBtn?.focus()
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
    // Restore focus
    previouslyFocusedElement?.focus()
    previouslyFocusedElement = null
  }
})

// Update help content when route changes while modal is open
watch(() => route.name, (newName) => {
  if (helpStore.isOpen && newName) {
    helpStore.open(newName as string)
  }
})

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="help-modal">
      <div
        v-if="helpStore.isOpen"
        class="help-backdrop"
        @click="handleBackdropClick"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-modal-title"
      >
        <div ref="containerRef" class="help-container">
          <!-- Header -->
          <header class="help-header">
            <div class="help-header-left">
              <!-- Menu toggle (hamburger) -->
              <button
                type="button"
                class="menu-button"
                aria-label="Sommaire"
                @click="helpStore.toggleNavigation()"
              >
                <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              </button>
              <!-- Page title -->
              <h2 id="help-modal-title" class="help-title">{{ helpStore.currentPageTitle || 'Aide' }}</h2>
            </div>
            <!-- Close button -->
            <button
              type="button"
              class="close-button"
              aria-label="Fermer l'aide"
              @click="handleClose"
            >
              <svg class="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <!-- Body -->
          <div class="help-body">
            <!-- Sidebar overlay (mobile) -->
            <div
              v-if="helpStore.showNavigation"
              class="sidebar-overlay"
              @click="helpStore.toggleNavigation()"
            />
            <!-- Sidebar navigation -->
            <aside v-show="helpStore.showNavigation" class="help-sidebar">
              <HelpNavigation />
            </aside>
            <!-- Content -->
            <main class="help-main">
              <HelpContent />
            </main>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Backdrop */
.help-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  padding: 20px;
}

/* Container */
.help-container {
  background: white;
  border-radius: 12px;
  width: 95vw;
  max-width: 1100px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

/* Header */
.help-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.help-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.help-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.menu-button,
.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: white;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.menu-button:hover,
.close-button:hover {
  background: #e9ecef;
  color: #212529;
}

.menu-button:focus-visible,
.close-button:focus-visible {
  outline: 2px solid #0066CC;
  outline-offset: 2px;
}

.menu-icon,
.close-icon {
  width: 20px;
  height: 20px;
}

/* Body */
.help-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

/* Sidebar overlay (mobile close) */
.sidebar-overlay {
  display: none;
}

@media (max-width: 768px) {
  .sidebar-overlay {
    display: block;
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 5;
  }
}

/* Sidebar */
.help-sidebar {
  width: 260px;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
  flex-shrink: 0;
}

/* Main content */
.help-main {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  min-width: 0;
}

/* Transitions */
.help-modal-enter-active,
.help-modal-leave-active {
  transition: opacity 0.3s ease;
}

.help-modal-enter-active .help-container,
.help-modal-leave-active .help-container {
  transition: transform 0.3s ease;
}

.help-modal-enter-from,
.help-modal-leave-to {
  opacity: 0;
}

.help-modal-enter-from .help-container,
.help-modal-leave-to .help-container {
  transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
  .help-backdrop {
    padding: 0;
  }

  .help-container {
    width: 100%;
    height: 100vh;
    max-width: none;
    border-radius: 0;
  }

  .help-header {
    padding: 12px 16px;
  }

  .help-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 280px;
    background: white;
    z-index: 10;
    box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);
  }

  .help-main {
    padding: 16px;
  }
}
</style>
